import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Col, Row, Spinner,
} from 'react-bootstrap';
import {
  RequestState,
  useAsyncOperationState,
} from '../../../../../dorian-shared/hooks/useAsyncOperationState';
import { Promotion } from '../../types/PromotionTypes';
import {
  RewardBannerButtonAction,
  RewardBannerFormValues,
  RewardBannerResponse,
} from './types/RewardBannerTypes';
import { api } from '../../../../api';
import { showToast } from '../../../../ui/utils';
import { ConfirmModal } from '../../../../ui/ConfirmModal/ConfirmModal';
import { RewardBannerForm } from './RewardBannerForm';
import {
  createEmptyRewardBannerFormValues,
  createRewardBannerFormData,
  rewardBannerToFormValues,
} from './rewardBannerUtils';

interface RewardBannerEditorProps {
  promotion: Promotion;
  isEditor: boolean;
  onSaved: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

function LoadingState(props: { requestState: RequestState }) {
  switch (props.requestState) {
    case RequestState.Loading:
      return (
        <div className="text-center py-3 mt-3">
          <Spinner animation="border" variant="primary" />
        </div>
      );
    case RequestState.Error:
      return (
        <Alert variant="danger" className="mb-0 mt-3">
          Failed to load Events configuration. Please try again later.
        </Alert>
      );
    default:
      return null;
  }
}

export function RewardBannerEditor(props: RewardBannerEditorProps) {
  const { promotion, isEditor, onSaved, onDirtyChange } = props;
  const promotionHasSchedule = Boolean(promotion.startTime && promotion.endTime);
  const [saving, setSaving] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [sectionExpanded, setSectionExpanded] = useState(false);
  const [formValues, setFormValues] = useState<RewardBannerFormValues>(
    createEmptyRewardBannerFormValues(),
  );

  const [requestState, {
    setToLoading,
    setToError,
    setToSuccess,
  }] = useAsyncOperationState();

  const loadRewardBanner = useCallback(() => {
    setToLoading();
    api.get<RewardBannerResponse>(`/v1/tools/promotions/${promotion.id}/reward-banner`)
      .then((response) => {
        const { rewardBanner } = response.data;
        setFormValues(
          rewardBanner
            ? rewardBannerToFormValues(rewardBanner)
            : createEmptyRewardBannerFormValues(),
        );
        setSectionExpanded(Boolean(rewardBanner));
        setToSuccess();
      })
      .catch(() => setToError());
  }, [promotion.id, setToError, setToLoading, setToSuccess]);

  useEffect(() => {
    loadRewardBanner();
  }, [loadRewardBanner]);

  const memoizedFormValues = useMemo(() => formValues, [formValues]);

  const handleSubmit = (values: RewardBannerFormValues) => {
    if (!promotionHasSchedule) {
      showToast({
        textMessage: 'Set promotion start and end times before saving Events configuration',
        variant: 'danger',
      });
      return;
    }

    if (values.buttonAction === RewardBannerButtonAction.Story && !promotion.storyUuid?.trim()) {
      showToast({
        textMessage: 'No story UUID configured on this promotion',
        variant: 'danger',
      });
      return;
    }

    const payload = values.buttonAction === RewardBannerButtonAction.Story && promotion.storyUuid
      ? {
        ...values,
        buttonTarget: JSON.stringify({ story_uuid: promotion.storyUuid.trim() }),
      }
      : values;

    setSaving(true);
    api.put(
      `/v1/tools/promotions/${promotion.id}/reward-banner`,
      createRewardBannerFormData(payload),
      {
        headers: {
          'content-type': 'multipart/form-data',
        },
      },
    )
      .then((response) => {
        const { rewardBanner } = response.data;
        if (rewardBanner) {
          setFormValues(rewardBannerToFormValues(rewardBanner));
          setSectionExpanded(true);
        }
        showToast({ textMessage: 'Events saved', variant: 'success' });
        onSaved();
      })
      .catch((err) => {
        showToast({
          textMessage: err?.response?.data?.error || 'Failed to save Events configuration',
          variant: 'danger',
        });
      })
      .finally(() => setSaving(false));
  };

  const hasConfiguredBanner = Boolean(formValues.id);

  const handleRemove = () => {
    if (!hasConfiguredBanner) {
      setFormValues(createEmptyRewardBannerFormValues());
      setSectionExpanded(false);
      setRemoveConfirmOpen(false);
      return;
    }

    setSaving(true);
    api.delete(`/v1/tools/promotions/${promotion.id}/reward-banner`)
      .then(() => {
        setFormValues(createEmptyRewardBannerFormValues());
        setSectionExpanded(false);
        setRemoveConfirmOpen(false);
        showToast({ textMessage: 'Events removed', variant: 'success' });
        onSaved();
      })
      .catch((err) => {
        showToast({
          textMessage: err?.response?.data?.error || 'Failed to remove Events configuration',
          variant: 'danger',
        });
      })
      .finally(() => setSaving(false));
  };

  if (requestState !== RequestState.Success) {
    return <LoadingState requestState={requestState} />;
  }

  return (
    <div className="mt-3">
      <ConfirmModal
        show={removeConfirmOpen}
        onHide={() => setRemoveConfirmOpen(false)}
        title="Remove Events?"
        confirmButtonTitle="Remove"
        onConfirm={handleRemove}
        onCancel={() => setRemoveConfirmOpen(false)}
        isLoading={saving}
      >
        This will delete the reward banner for this promotion. This cannot be undone.
      </ConfirmModal>

      {!promotionHasSchedule && isEditor && (
        <Alert variant="warning">
          Set the promotion start and end times above before configuring Events.
          Event timing follows the promotion schedule.
        </Alert>
      )}

      {sectionExpanded && isEditor ? (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h5 className="mb-0 text-dark font-weight-bold">Reward Banner</h5>
          <Button
            type="button"
            variant={null as any}
            className="text-white border-0"
            style={{ backgroundColor: '#888888' }}
            onClick={() => setRemoveConfirmOpen(true)}
            disabled={saving}
          >
            Remove Events
          </Button>
        </div>
      ) : (
        <h5 className="mb-3 text-dark font-weight-bold">Reward Banner</h5>
      )}

      {!sectionExpanded && (
        <Row className="mb-3">
          <Col>
            <Button
              type="button"
              onClick={() => setSectionExpanded(true)}
              disabled={!isEditor}
            >
              Add Reward Banner
            </Button>
          </Col>
        </Row>
      )}

      {sectionExpanded && (
        <RewardBannerForm
          formValues={memoizedFormValues}
          onSubmit={handleSubmit}
          isLoading={saving}
          submitButtonText="Save Events"
          disabled={!isEditor || !promotionHasSchedule}
          promotionStoryUuid={promotion.storyUuid}
          onDirtyChange={onDirtyChange}
        />
      )}
    </div>
  );
}
